import axios from 'axios';
import { deleteCookie, getCookie, setCookie } from '../utils/cookies';
import { accessTokenKey, authRedirectReason, refreshTokenKey, usernameKey } from './AuthInterceptor';

let isRefreshing = false;
let failedQueue: Array<{ resolve: () => void; reject: (err?: unknown) => void }> = [];

const processQueue = (error?: unknown) => {
    failedQueue.forEach(p => error ? p.reject(error) : p.resolve());
    failedQueue = [];
};

const doRefresh = async (): Promise<boolean> => {
    const refreshToken = getCookie(refreshTokenKey);
    if (!refreshToken) return false;
    try {
        const res = await axios.post('/auth/refresh', {token: refreshToken});
        if (res.status === 200) {
            const {accessToken, refreshToken: newRefreshToken, username} = res.data;
            setCookie(accessTokenKey, accessToken, 30);
            setCookie(refreshTokenKey, newRefreshToken, 30);
            setCookie(usernameKey, username, 30);
            return true;
        }
    } catch (e) {
        console.error(e)
    }
    return false;
};

const cleanupAndNotify = () => {
    deleteCookie(accessTokenKey);
    deleteCookie(refreshTokenKey);
    deleteCookie(usernameKey);
    localStorage.setItem(authRedirectReason, 'unauthorized');
    window.dispatchEvent(new Event('session-expired'));
};

type FetchArgs = Parameters<typeof fetch>;

export const authorizedFetch = async (...args: FetchArgs): Promise<Response> => {
    const [input, init] = args;

    const call = async (): Promise<Response> => {
        const headers = new Headers((init && init.headers) || {});
        const token = getCookie(accessTokenKey);
        if (token) headers.set('Authorization', `Bearer ${token}`);
        return await fetch(input, {...init, headers});
    };

    const first = await call();
    if (first.status !== 401) return first;

    if (isRefreshing) {
        await new Promise<void>((resolve, reject) => failedQueue.push({resolve, reject}));
        const retried = await call();
        if (retried.status === 401) {
            cleanupAndNotify();
            throw new Error('Unauthorized after refresh (queued)');
        }
        return retried;
    }

    isRefreshing = true;
    const ok = await doRefresh();
    isRefreshing = false;
    ok ? processQueue() : processQueue(new Error('Refresh failed'));

    if (!ok) {
        cleanupAndNotify();
        throw new Error('Unauthorized and refresh failed');
    }

    const retried = await call();
    if (retried.status === 401) {
        cleanupAndNotify();
        throw new Error('Unauthorized after refresh');
    }
    return retried;
};

export default authorizedFetch;
